import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { anatomicalRegionConstants, isocenterTypeConstants } from '../../constants/simulator-constants';
import { IAnatomicalModel } from '../../models';

export interface ModalProps {
  size: string;
  centered: boolean;
  vertical: boolean;
  fixed: string;
  isOpen: boolean;
  toggle(): void;
  initialType: string;
  children?: React.ReactNode;
  anatomicalModelId: number;
  type: string;
  onSaveClickHandler?: (checked: string, isocenterType: string) => void;
  isocenterInitialValue: number;
  anatomicalModel: IAnatomicalModel;
}

export const IsocenterModal: React.FC<ModalProps> = (props: ModalProps): React.ReactElement => {
  const {
    size,
    centered,
    vertical,
    fixed,
    isOpen,
    toggle,
    onSaveClickHandler,
    type,
    initialType,
    isocenterInitialValue,
    anatomicalModel,
  } = props;
  const contentRef = useRef<HTMLDivElement>(null);

  const [isocenterType, setIsocenterType] = useState(isocenterTypeConstants.whole_body);
  const [checked, setChecked] = useState<number>();

  useEffect(() => {
    setIsocenterType(type);
    if (initialType === type) {
      setChecked(isocenterInitialValue);
    }
  }, [type, isocenterInitialValue, initialType]);

  useEffect(() => {
    // on isocenter type change set the default value for that isocenter type
    switch (isocenterType) {
      case isocenterTypeConstants.regional:
        setChecked(
          initialType === isocenterType
            ? isocenterInitialValue
            : anatomicalModel.regions.find((r) => r.name === anatomicalRegionConstants.spine)?.id
        );
        break;
      case isocenterTypeConstants.whole_body:
        setChecked(initialType === isocenterType ? isocenterInitialValue : 180);
        break;
      default:
        break;
    }
  }, [isocenterType, initialType, isocenterInitialValue, anatomicalModel?.regions]);

  let el = document.getElementById('modal-root');

  if (!el && isOpen) {
    el = document.createElement('div');
    el.id = 'modal-root';
    el.style.display = 'contents';
    el.style.position = 'relative';
    el.style.zIndex = '1050';
    document.body.classList.add('modal-open');
    document.body.appendChild(el);
  }

  useEffect(() => {
    const modal = document.getElementById('modal-root');
    if (isOpen) {
      setTimeout(() => {
        if (modal) {
          const fc = modal.firstChild as HTMLElement | null;
          fc?.classList.add('show');
          const lc = modal.lastChild as HTMLElement | null;
          lc?.classList.add('show');
        }
      }, 100);
    }

    return () => {
      if (isOpen) {
        if (modal) {
          document.body.removeChild(modal);
        }
        document.body.classList.remove('modal-open');
      }
    };
  }, [isOpen]);

  const modalClasses = `modal fade  ${fixed !== '' ? `fixed-${fixed}` : ' '}`;
  const modalStyle = isOpen ? { display: 'block' } : {};

  const dialogClasses = `modal-dialog modal-${size} ${centered ? 'modal-dialog-centered' : ''} ${
    vertical ? 'modal-dialog-vertical' : ''
  }`;

  const onHideClickHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    if (el && (e.target as HTMLElement).classList.contains('modal')) {
      (el.firstChild as HTMLElement).classList.remove('show');
      (el.lastChild as HTMLElement).classList.remove('show');
      setTimeout(() => {
        toggle();
        document.body.classList.remove('modal-open');
      }, 100);
    }
  };

  const onCloseClickHandler = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (el) {
      const fc = el.firstChild as HTMLElement | null;
      fc?.classList.remove('show');
      const lc = el.lastChild as HTMLElement | null;
      lc?.classList.remove('show');
    }

    setTimeout(() => {
      toggle();
      document.body.classList.remove('modal-open');
    }, 100);
  };

  const setWholeBody = (e: React.MouseEvent) => {
    setIsocenterType(isocenterTypeConstants.whole_body);
    e.stopPropagation();
  };

  const setRegion = (e: React.MouseEvent) => {
    setIsocenterType(isocenterTypeConstants.regional);
    e.stopPropagation();
  };

  const handleChange = (val: number | undefined) => {
    setChecked(val);
  };

  const getAnatomicalModelRegionsId = (regionName: string) => {
    return anatomicalModel?.regions.find((r) => r.name === regionName)?.id || -1;
  };

  const isRegionChecked = (regionName: string) => {
    const region = anatomicalModel?.regions.find((r) => r.name === regionName);
    return region && region?.id === checked;
  };

  const onSaveHandler = () => {
    if (onSaveClickHandler && checked) onSaveClickHandler(checked.toString(), isocenterType);
  };

  return (
    <div>
      {isOpen &&
        el &&
        ReactDOM.createPortal(
          <>
            <div role="presentation" className={modalClasses} style={modalStyle} onClick={onHideClickHandler}>
              <div className={dialogClasses} role="document">
                <div className="modal-content isocenter-modal">
                  <span className="my-3 mx-5 isocenter-modal-title">Set iso-center</span>
                  <span className="mx-5 w-50 isocenter-modal-subtitle">
                    Select iso-center for the patient. You can choose between whole body or specific regions.
                  </span>
                  <div className="modal-body py-0 mt-5" ref={contentRef}>
                    <div className="row mx-3">
                      <div className="col-6 whole-body-wrapper">
                        <div
                          className="text-center title-wrapper mb-2"
                          onClick={setWholeBody}
                          onKeyDown={() => {}}
                          role="button"
                          tabIndex={0}
                        >
                          <input
                            type="radio"
                            name={isocenterTypeConstants.whole_body}
                            value={isocenterTypeConstants.whole_body}
                            checked={isocenterType === isocenterTypeConstants.whole_body}
                            onChange={() => {}}
                          />
                          <span className="title ms-2">Whole body</span>
                        </div>
                        <div
                          className={`whole-body ${
                            isocenterType === isocenterTypeConstants.regional ? 'fade-isocenter' : ''
                          }`}
                        >
                          <div
                            onClick={(e) => {
                              handleChange(180);
                              e.stopPropagation();
                            }}
                            onKeyDown={() => handleChange(180)}
                            role="button"
                            tabIndex={0}
                            className="w-100"
                          >
                            <p className="text-end">
                              180cm{' '}
                              <span>
                                <input
                                  type="radio"
                                  id="180cm"
                                  name="180cm"
                                  value="180cm"
                                  onChange={() => {}}
                                  checked={checked === 180 || !checked}
                                />
                              </span>
                            </p>
                          </div>
                          <div
                            onClick={(e) => {
                              handleChange(140);
                              e.stopPropagation();
                            }}
                            onKeyDown={() => handleChange(140)}
                            role="button"
                            tabIndex={0}
                            className="w-100"
                          >
                            <p className="text-end">
                              140cm{' '}
                              <span>
                                <input
                                  type="radio"
                                  id="140cm"
                                  name="140cm"
                                  value="140cm"
                                  onChange={() => {}}
                                  checked={checked === 140}
                                />
                              </span>
                            </p>
                          </div>
                          <div
                            onClick={(e) => {
                              handleChange(100);
                              e.stopPropagation();
                            }}
                            onKeyDown={() => handleChange(100)}
                            role="button"
                            tabIndex={0}
                            className="w-100"
                          >
                            <p className="text-end">
                              100cm{' '}
                              <span>
                                <input
                                  type="radio"
                                  id="100cm"
                                  name="100cm"
                                  value="100cm"
                                  onChange={() => {}}
                                  checked={checked === 100}
                                />
                              </span>
                            </p>
                          </div>
                          <div
                            onClick={(e) => {
                              handleChange(60);
                              e.stopPropagation();
                            }}
                            onKeyDown={() => handleChange(60)}
                            role="button"
                            tabIndex={0}
                            className="w-100"
                          >
                            <p className="text-end">
                              60cm{' '}
                              <span>
                                <input
                                  type="radio"
                                  id="60cm"
                                  name="60cm"
                                  value="60cm"
                                  onChange={() => {}}
                                  checked={checked === 60}
                                />
                              </span>
                            </p>
                          </div>
                          <div
                            onClick={(e) => {
                              handleChange(20);
                              e.stopPropagation();
                            }}
                            onKeyDown={() => handleChange(20)}
                            role="button"
                            tabIndex={0}
                            className="w-100"
                          >
                            <p className="text-end">
                              20cm{' '}
                              <span>
                                <input
                                  type="radio"
                                  id="20cm"
                                  name="20cm"
                                  value="20cm"
                                  onChange={() => {}}
                                  checked={checked === 20}
                                />
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="col-6 regions-wrapper title-wrapper">
                        <div
                          className="text-center title-wrapper mb-2"
                          onClick={setRegion}
                          onKeyDown={() => {}}
                          role="button"
                          tabIndex={0}
                        >
                          <input
                            type="radio"
                            name="regions"
                            value="regions"
                            checked={isocenterType === isocenterTypeConstants.regional}
                            onChange={() => {}}
                          />
                          <span className="title ms-2">Select regions</span>
                        </div>
                        <div
                          className={`regions ${
                            isocenterType === isocenterTypeConstants.whole_body ? 'fade-isocenter' : ''
                          }`}
                        >
                          {/* Spine */}
                          <div
                            onClick={(e) => {
                              handleChange(getAnatomicalModelRegionsId(anatomicalRegionConstants.spine));
                              e.stopPropagation();
                            }}
                            onKeyDown={() => handleChange(getAnatomicalModelRegionsId(anatomicalRegionConstants.spine))}
                            role="button"
                            tabIndex={0}
                            className="w-100"
                          >
                            <p className="w-100 text-start">
                              Spine @ Isocenter 130 cm
                              <span>
                                <input
                                  type="radio"
                                  id="spine"
                                  name="spine"
                                  value="spine"
                                  onChange={() => {}}
                                  checked={isRegionChecked(anatomicalRegionConstants.spine)}
                                />
                              </span>
                            </p>
                          </div>

                          {/* Abdomen */}
                          <div
                            onClick={(e) => {
                              handleChange(getAnatomicalModelRegionsId(anatomicalRegionConstants.abdomen));
                              e.stopPropagation();
                            }}
                            onKeyDown={() =>
                              handleChange(getAnatomicalModelRegionsId(anatomicalRegionConstants.abdomen))
                            }
                            role="button"
                            tabIndex={0}
                            className="w-100"
                          >
                            <p className="text-start">
                              Abdomen @ Isocenter 130 cm
                              <span>
                                <input
                                  type="radio"
                                  id="abdomen"
                                  name="abdomen"
                                  value="abdomen"
                                  onChange={() => {}}
                                  checked={isRegionChecked(anatomicalRegionConstants.abdomen)}
                                />
                              </span>
                            </p>
                          </div>

                          {/* Left shoulder */}
                          <div
                            onClick={(e) => {
                              handleChange(
                                getAnatomicalModelRegionsId(anatomicalRegionConstants.left_shoulder_and_elbow)
                              );
                              e.stopPropagation();
                            }}
                            onKeyDown={() =>
                              handleChange(
                                getAnatomicalModelRegionsId(anatomicalRegionConstants.left_shoulder_and_elbow)
                              )
                            }
                            role="button"
                            tabIndex={0}
                            className="w-100"
                          >
                            <p className="text-start">
                              Left shoulder and elbow @ Isocenter 134 cm
                              <span>
                                <input
                                  type="radio"
                                  id="leftShoulder"
                                  name="leftShoulder"
                                  value="leftShoulder"
                                  onChange={() => {}}
                                  checked={isRegionChecked(anatomicalRegionConstants.left_shoulder_and_elbow)}
                                />
                              </span>
                            </p>
                          </div>

                          {/* Pelvis */}
                          <div
                            onClick={(e) => {
                              handleChange(getAnatomicalModelRegionsId(anatomicalRegionConstants.pelvis));
                              e.stopPropagation();
                            }}
                            onKeyDown={() =>
                              handleChange(getAnatomicalModelRegionsId(anatomicalRegionConstants.pelvis))
                            }
                            role="button"
                            tabIndex={0}
                            className="w-100"
                          >
                            <p className="text-start">
                              Pelvis @ Isocenter 88 cm
                              <span>
                                <input
                                  type="radio"
                                  id="pelvis"
                                  name="pelvis"
                                  value="pelvis"
                                  onChange={() => {}}
                                  checked={isRegionChecked(anatomicalRegionConstants.pelvis)}
                                />
                              </span>
                            </p>
                          </div>

                          {/* Left hand */}
                          <div
                            onClick={(e) => {
                              handleChange(getAnatomicalModelRegionsId(anatomicalRegionConstants.left_hand));
                              e.stopPropagation();
                            }}
                            onKeyDown={() =>
                              handleChange(getAnatomicalModelRegionsId(anatomicalRegionConstants.left_hand))
                            }
                            role="button"
                            tabIndex={0}
                            className="w-100"
                          >
                            <p className="text-start">
                              Left hand @ Isocenter 93 cm
                              <span>
                                <input
                                  type="radio"
                                  id="leftHand"
                                  name="leftHand"
                                  value="leftHand"
                                  onChange={() => {}}
                                  checked={isRegionChecked(anatomicalRegionConstants.left_hand)}
                                />
                              </span>
                            </p>
                          </div>

                          {/* Right knee */}
                          <div
                            onClick={(e) => {
                              handleChange(13);
                              e.stopPropagation();
                            }}
                            onKeyDown={() => handleChange(13)}
                            role="button"
                            tabIndex={0}
                            className={`w-100 ${
                              anatomicalModel?.regions?.some((r) => r.name === anatomicalRegionConstants.right_knee)
                                ? ''
                                : 'd-none'
                            }`}
                          >
                            <p className="w-100 text-start">
                              Right knee @ Isocenter 60 cm
                              <span>
                                <input
                                  type="radio"
                                  id="rightKnee"
                                  name="rightKnee"
                                  value="rightKnee"
                                  onChange={() => {}}
                                  checked={isRegionChecked(anatomicalRegionConstants.right_knee)}
                                />
                              </span>
                            </p>
                          </div>

                          {/* Right foot */}
                          <div
                            onClick={(e) => {
                              handleChange(getAnatomicalModelRegionsId('Right foot'));
                              e.stopPropagation();
                            }}
                            onKeyDown={() => handleChange(getAnatomicalModelRegionsId('Right foot'))}
                            role="button"
                            tabIndex={0}
                            className="w-100"
                          >
                            <p className="w-100 text-start">
                              Right foot @ Isocenter 11 cm
                              <span>
                                <input
                                  type="radio"
                                  id="rightFoot"
                                  name="rightFoot"
                                  value="rightFoot"
                                  onChange={() => {}}
                                  checked={isRegionChecked(anatomicalRegionConstants.right_foot)}
                                />
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="isocenter-buttons">
                      <button
                        type="button"
                        className="btn btn-md btn-light cancel-btn px-4"
                        aria-label="Close"
                        onClick={onCloseClickHandler}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        aria-label="Save"
                        className="btn btn-md btn-primary ms-2 simulator-btn-primary px-4"
                        onClick={onSaveHandler}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade" />
          </>,
          el
        )}
    </div>
  );
};

IsocenterModal.defaultProps = {};
